import { useState } from 'react'
import { Eye, EyeOff, Link2, Plus, X } from 'lucide-react'
import { Input } from '@/components/ui/input'

/** Accès d'une équipe du client à son atelier de préparation (workshop.qolaig.app). */
export interface WorkshopTeamLinkInput {
  equipe: string
  url: string
  password: string
}

export const EMPTY_TEAM_LINK: WorkshopTeamLinkInput = { equipe: '', url: '', password: '' }

/** Lignes réellement saisies, prêtes à envoyer à l'API (lignes vides ignorées). */
export function toTeamLinksPayload(links: WorkshopTeamLinkInput[] | undefined) {
  return (links || [])
    .map((link) => ({
      equipe: link.equipe.trim(),
      url: link.url.trim(),
      password: link.password.trim() || null,
    }))
    .filter((link) => link.equipe || link.url)
}

interface WorkshopTeamLinksFieldProps {
  links: WorkshopTeamLinkInput[]
  visibleClient: boolean
  onLinksChange: (links: WorkshopTeamLinkInput[]) => void
  onVisibleClientChange: (visible: boolean) => void
}

/** Saisie admin des accès workshop.qolaig.app par équipe + affichage dans l'espace client. */
export function WorkshopTeamLinksField({
  links,
  visibleClient,
  onLinksChange,
  onVisibleClientChange,
}: WorkshopTeamLinksFieldProps) {
  const [shownPasswords, setShownPasswords] = useState<Record<number, boolean>>({})
  const rows = links.length > 0 ? links : [EMPTY_TEAM_LINK]

  const updateRow = (index: number, field: keyof WorkshopTeamLinkInput, value: string) => {
    onLinksChange(rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }
  const removeRow = (index: number) => {
    const next = rows.filter((_, i) => i !== index)
    onLinksChange(next.length > 0 ? next : [EMPTY_TEAM_LINK])
  }

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 p-4">
      <div>
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <Link2 className="h-4 w-4 text-cyan-600" />
          Accès à l'atelier de préparation (workshop.qolaig.app)
        </div>
        <p className="mt-1 text-xs text-gray-500">Un lien et un mot de passe par équipe du client.</p>
      </div>

      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-1 gap-2 rounded-lg bg-white p-2 sm:grid-cols-[140px_minmax(0,1fr)_170px_auto]">
            <Input
              type="text"
              value={row.equipe}
              onChange={(e) => updateRow(index, 'equipe', e.target.value)}
              placeholder="Équipe (ex. Commerce)"
              aria-label={`Nom de l'équipe ${index + 1}`}
              className="bg-gray-50 border-gray-200"
            />
            <Input
              type="url"
              value={row.url}
              onChange={(e) => updateRow(index, 'url', e.target.value)}
              placeholder="https://workshop.qolaig.app/..."
              aria-label={`Lien de l'équipe ${index + 1}`}
              className="bg-gray-50 border-gray-200"
            />
            <Input
              type={shownPasswords[index] ? 'text' : 'password'}
              value={row.password}
              onChange={(e) => updateRow(index, 'password', e.target.value)}
              placeholder="Mot de passe"
              aria-label={`Mot de passe de l'équipe ${index + 1}`}
              autoComplete="off"
              className="bg-gray-50 border-gray-200 font-mono"
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShownPasswords((s) => ({ ...s, [index]: !s[index] }))}
                  className="text-gray-400 hover:text-gray-700"
                  aria-label={shownPasswords[index] ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {shownPasswords[index] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              }
            />
            <button
              type="button"
              onClick={() => removeRow(index)}
              className="flex items-center justify-center rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
              aria-label={`Retirer l'équipe ${index + 1}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onLinksChange([...rows, { ...EMPTY_TEAM_LINK }])}
          className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
        >
          <Plus className="h-4 w-4" />
          Ajouter une équipe
        </button>
      </div>

      <label className="flex items-start gap-2.5 border-t border-gray-200 pt-3 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={visibleClient}
          onChange={(e) => onVisibleClientChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
        />
        <span>
          <span className="font-medium">Afficher dans l'espace client</span>
          <span className="block text-xs text-gray-500">
            Le client voit ces accès sur sa page « Mes projets » et peut copier les mots de passe.
          </span>
        </span>
      </label>
    </div>
  )
}
